import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { Button } from '@/components/ui/button'
import { useProjectVersions } from '@/features/versions/hooks/useProjectVersions'
import { activeVersionOf } from '@/features/versions/lib/activeVersion'
import { useAppDispatch, useAppSelector } from '@/store'
import { bulkImportRequirements, selectBulkImportResult, selectRequirementsError } from '../slices/requirementsSlice'

/**
 * AC-6/Decision 4 — bulk import, as the endpoint is actually built: a
 * version-scoped "import from discovery" action with a confirm step, posting
 * `{ versionId }` and nothing else. There is no paste-rows form and no
 * explicit version chooser — the active discovery cycle is resolved the same
 * way the Interviews page resolves it, reusing the already-live
 * `features/versions` slice (no second fetch of an endpoint that slice does
 * not already own).
 *
 * Deferred finding c2-1 (TASK-71426B1C): a refused import renders its OWN
 * inline refusal here (`'bulk-import'`-scoped `requirementsSlice.error`) —
 * this panel triggered the request, so it owns the failure, and the register
 * table beside it stays mounted (the unscoped list-read channel is untouched).
 */
export function BulkImportPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  // TASK-3D6DB8B6 fix cycle 1: ONLY this project's versions — never the
  // previous project's rows still in the slice — so the import target (and
  // the versionId it posts) always belongs to `projectId`.
  const versions = useProjectVersions(orgId, projectId)
  const result = useAppSelector(selectBulkImportResult)
  const error = useAppSelector(selectRequirementsError)
  // The confirm step is keyed by project: a confirm opened on A never
  // re-appears, unasked, once B's versions land.
  const [confirmingFor, setConfirmingFor] = useState<string | null>(null)
  const confirming = confirmingFor === projectId
  const [busy, setBusy] = useState(false)

  // Only this panel's own `'bulk-import'`-scoped refusal (see the JSDoc above).
  const bulkImportError = error?.scope === 'bulk-import' ? error : null

  const activeVersion = useMemo(() => activeVersionOf(versions), [versions])

  const onTriggerClicked = (): void => setConfirmingFor(projectId)
  const onCancelClicked = (): void => setConfirmingFor(null)
  const onConfirmClicked = (): void => {
    if (activeVersion === null) return
    setBusy(true)
    void dispatch(bulkImportRequirements({ orgId, projectId, versionId: activeVersion.id })).then(() => {
      setBusy(false)
      setConfirmingFor(null)
    })
  }

  return (
    <section aria-labelledby="bulk-import-heading" className="rounded-lg border border-border bg-card px-4 py-4">
      <SectionLabel>{t('requirements.bulkImport.label')}</SectionLabel>
      <h2 id="bulk-import-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
        {t('requirements.bulkImport.heading')}
      </h2>
      <p className="mt-1.5 text-[13px] text-ink-soft">{t('requirements.bulkImport.body')}</p>

      {activeVersion === null ? (
        <p className="mt-3 text-xs text-muted-foreground">{t('requirements.bulkImport.noVersion')}</p>
      ) : confirming ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-[13px] text-foreground">
            {t('requirements.bulkImport.confirmPrompt', { version: activeVersion.name })}
          </p>
          <Button size="sm" disabled={busy} onClick={onConfirmClicked} data-testid="bulk-import-confirm">
            {t('requirements.bulkImport.confirm')}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onCancelClicked}>
            {t('requirements.actions.cancel')}
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="mt-3" onClick={onTriggerClicked} data-testid="bulk-import-trigger">
          {t('requirements.bulkImport.trigger', { version: activeVersion.name })}
        </Button>
      )}

      {bulkImportError !== null && (
        <div role="alert" data-testid="bulk-import-error" className="mt-3 rounded-md border border-border bg-card px-3 py-2">
          <p className="text-xs text-blocked">{t('requirements.actions.refusedLede')}</p>
          <dl className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
            {bulkImportError.status > 0 && (
              <div className="flex items-center gap-1">
                <dt>{t('common:states.statusLabel')}</dt>
                <dd className="v-figure tabular-nums text-foreground">{bulkImportError.status}</dd>
              </div>
            )}
            {bulkImportError.code !== undefined && (
              <div className="flex items-center gap-1">
                <dt>{t('common:states.codeLabel')}</dt>
                <dd className="font-mono text-foreground">{bulkImportError.code}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {result !== null && (
        <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-2.5 text-center" data-testid="bulk-import-result">
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">
              {t('requirements.bulkImport.created')}
            </dt>
            <dd className="v-figure text-lg font-semibold tabular-nums text-foreground">{result.created}</dd>
          </div>
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">
              {t('requirements.bulkImport.skippedExisting')}
            </dt>
            <dd className="v-figure text-lg font-semibold tabular-nums text-foreground">{result.skippedExisting}</dd>
          </div>
          <div>
            <dt className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">
              {t('requirements.bulkImport.elementsWithNoBullets')}
            </dt>
            <dd className="v-figure text-lg font-semibold tabular-nums text-foreground">{result.elementsWithNoBullets}</dd>
          </div>
          <p className="col-span-3 mt-1 text-xs text-muted-foreground">
            {result.created === 0 && result.skippedExisting === 0 && result.elementsWithNoBullets === 0
              ? t('requirements.bulkImport.nothingConcluded')
              : result.created === 0 && result.skippedExisting > 0
                ? t('requirements.bulkImport.alreadyImported')
                : result.elementsWithNoBullets > 0
                  ? t('requirements.bulkImport.noBullets')
                  : t('requirements.bulkImport.imported', { count: result.created })}
          </p>
        </dl>
      )}
    </section>
  )
}
